import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Checkbox } from "@/components/ui/checkbox"
import { apiRequest, type ApiError } from "@/lib/api"
import type { Project, Segment, Workflow, WorkflowChannel } from "@/types"
import {
  Plus,
  Workflow as WorkflowIcon,
  Settings2,
  Play,
  Loader2,
  Clock,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Copy,
  Pencil,
  Trash2,
  MoreVertical,
  RefreshCw,
  Power,
  PowerOff,
  ImagePlus,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  COPY_LANGUAGES,
  DEFAULT_COPY_LANGUAGE,
  DEFAULT_TIMEZONE,
  RECURRING_TIMEZONES,
  copyLanguageLabel,
  timezoneAbbreviation,
  type CopyLanguage,
  type RecurringTimezone,
} from "@/lib/recurring-broadcast-options"

type WorkflowsResponse = {
  workflows: Workflow[]
}

type BroadcastsPageProps = {
  currentProject: Project | null
}

const AVAILABLE_HOURS = Array.from({ length: 24 }, (_, i) => i)

const BroadcastsPage = ({ currentProject }: BroadcastsPageProps) => {
  const { projectSlug } = useParams<{ projectSlug: string }>()
  const queryClient = useQueryClient()
  const [newName, setNewName] = useState("")
  const [newChannel, setNewChannel] = useState<WorkflowChannel>("push")
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [deleteWorkflowId, setDeleteWorkflowId] = useState<number | null>(null)
  const [renameWorkflow, setRenameWorkflow] = useState<{ id: number; name: string } | null>(null)

  const [isRecurringDialogOpen, setIsRecurringDialogOpen] = useState(false)
  const [recurringName, setRecurringName] = useState("")
  const [recurringUrls, setRecurringUrls] = useState("")
  const [recurringHours, setRecurringHours] = useState<number[]>([])
  const [recurringEndDate, setRecurringEndDate] = useState("")
  const [recurringImages, setRecurringImages] = useState<{ url: string; filename: string }[]>([])
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [recurringTargetType, setRecurringTargetType] = useState<"all" | "segment">("all")
  const [recurringSegmentIds, setRecurringSegmentIds] = useState<number[]>([])
  const [recurringTimezone, setRecurringTimezone] = useState<RecurringTimezone>(DEFAULT_TIMEZONE)
  const [recurringCopyLanguage, setRecurringCopyLanguage] = useState<CopyLanguage>(DEFAULT_COPY_LANGUAGE)

  const basePath = `/api/projects/${encodeURIComponent(projectSlug || "")}/workflows`
  const workflowsPath = `${basePath}?workflow_type=broadcast,recurring_broadcast`

  const { data, isLoading, isError, error } = useQuery<WorkflowsResponse, ApiError>({
    queryKey: ["workflows", projectSlug, "broadcast"],
    queryFn: () => apiRequest<WorkflowsResponse>(workflowsPath),
    enabled: Boolean(projectSlug),
  })

  const workflows = data?.workflows ?? []

  const segmentsQuery = useQuery<{ segments: Segment[] }, ApiError>({
    queryKey: ["segments", projectSlug],
    queryFn: () => apiRequest<{ segments: Segment[] }>(`/api/projects/${encodeURIComponent(projectSlug || "")}/segments`),
    enabled: Boolean(projectSlug),
  })
  const segments = segmentsQuery.data?.segments ?? []

  const createMutation = useMutation<{ workflow: Workflow }, ApiError>({
    mutationFn: () =>
      apiRequest<{ workflow: Workflow }>(basePath, {
        method: "POST",
        body: JSON.stringify({ workflow: { name: newName.trim(), workflow_type: "broadcast", channel: newChannel } }),
      }),
    onSuccess: () => {
      setNewName("")
      setNewChannel("push")
      setIsDialogOpen(false)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const duplicateMutation = useMutation<{ workflow: Workflow }, ApiError, number>({
    mutationFn: (workflowId) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${workflowId}/duplicate`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const renameMutation = useMutation<{ workflow: Workflow }, ApiError, { id: number; name: string }>({
    mutationFn: ({ id, name }) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ workflow: { name } }),
      }),
    onSuccess: () => {
      setRenameWorkflow(null)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const createRecurringMutation = useMutation<{ workflow: Workflow }, ApiError>({
    mutationFn: () => {
      const urls = recurringUrls.split("\n").map(u => u.trim()).filter(Boolean)
      const contactFilter = recurringTargetType === "segment" && recurringSegmentIds.length > 0
        ? { target_type: "segment", selected_segment_ids: recurringSegmentIds }
        : {}
      return apiRequest<{ workflow: Workflow }>(basePath, {
        method: "POST",
        body: JSON.stringify({
          workflow: {
            name: recurringName.trim(),
            workflow_type: "recurring_broadcast",
            channel: "push",
            destination_urls: urls,
            schedule_hours: recurringHours,
            timezone: recurringTimezone,
            copy_language: recurringCopyLanguage,
            recurrence_end_at: recurringEndDate || null,
            uploaded_images: recurringImages.map(img => img.url),
            contact_filter: contactFilter,
          },
        }),
      })
    },
    onSuccess: () => {
      setRecurringName("")
      setRecurringUrls("")
      setRecurringHours([])
      setRecurringEndDate("")
      setRecurringImages([])
      setRecurringTargetType("all")
      setRecurringSegmentIds([])
      setRecurringTimezone(DEFAULT_TIMEZONE)
      setRecurringCopyLanguage(DEFAULT_COPY_LANGUAGE)
      setIsRecurringDialogOpen(false)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const toggleStatusMutation = useMutation<{ workflow: Workflow }, ApiError, { id: number; status: string }>({
    mutationFn: ({ id, status }) =>
      apiRequest<{ workflow: Workflow }>(`${basePath}/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ workflow: { status } }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const deleteMutation = useMutation<void, ApiError, number>({
    mutationFn: (workflowId) =>
      apiRequest<void>(`${basePath}/${workflowId}`, { method: "DELETE" }),
    onSuccess: () => {
      setDeleteWorkflowId(null)
      queryClient.invalidateQueries({ queryKey: ["workflows", projectSlug, "broadcast"] })
    },
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) return
    createMutation.mutate()
  }

  const handleCreateRecurring = (e: React.FormEvent) => {
    e.preventDefault()
    if (!recurringName.trim() || !recurringUrls.trim() || recurringHours.length === 0) return
    createRecurringMutation.mutate()
  }

  const toggleHour = (hour: number) => {
    setRecurringHours(prev =>
      prev.includes(hour) ? prev.filter(h => h !== hour) : [...prev, hour].sort((a, b) => a - b)
    )
  }

  const formatHour = (h: number) => `${String(h).padStart(2, "0")}:00`

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setIsUploadingImage(true)
    setUploadError(null)

    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('image', file)

        const response = await fetch(`/api/projects/${encodeURIComponent(projectSlug || "")}/workflow_images`, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
        })

        if (response.ok) {
          const data = await response.json()
          setRecurringImages(prev => [...prev, { url: data.workflow_image.url, filename: data.workflow_image.filename }])
        } else {
          const errData = await response.json().catch(() => null)
          const msg = errData?.errors?.join(", ") || errData?.error || `Upload falhou (${response.status})`
          setUploadError(msg)
        }
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Erro ao fazer upload da imagem")
    } finally {
      setIsUploadingImage(false)
      if (e.target) e.target.value = ''
    }
  }

  const removeImage = (index: number) => {
    setRecurringImages(prev => prev.filter((_, i) => i !== index))
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle2 className="h-4 w-4" />
      case "draft":
        return <Clock className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 border-green-200"
      case "draft":
        return "bg-gray-50 text-gray-700 border-gray-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Nunca"
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return "Nunca"
    return new Intl.DateTimeFormat("pt-BR", {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const channelLabel = (channel: WorkflowChannel) => (
    channel === "facebook_message" ? "Messenger" : "Push"
  )

  return (
    <div className="flex flex-col gap-6 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Broadcasts</h1>
          <p className="mt-1 text-base text-muted-foreground">
            Projete sequências de mensagens e automatize suas transmissões
          </p>
        </div>

        <div className="flex gap-2">
          <Dialog open={isRecurringDialogOpen} onOpenChange={setIsRecurringDialogOpen}>
            <DialogTrigger asChild>
              <Button size="lg" variant="outline" className="gap-2">
                <RefreshCw className="h-5 w-5" />
                Novo Recorrente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateRecurring}>
                <DialogHeader>
                  <DialogTitle>Criar Broadcast Recorrente</DialogTitle>
                  <DialogDescription>
                    Configure envios automáticos com copies geradas por IA.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      placeholder="ex.: Promoções Diárias, Novidades do Blog"
                      value={recurringName}
                      onChange={(e) => setRecurringName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>URLs de destino (uma por linha)</Label>
                    <Textarea
                      placeholder={"https://meusite.com/pagina-1\nhttps://meusite.com/pagina-2\nhttps://meusite.com/pagina-3"}
                      value={recurringUrls}
                      onChange={(e) => setRecurringUrls(e.target.value)}
                      rows={4}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">As URLs serão rotacionadas automaticamente a cada envio.</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Imagens (opcional)</Label>
                    <div className="flex flex-wrap gap-2">
                      {recurringImages.map((img, idx) => (
                        <div key={idx} className="group relative h-20 w-20 overflow-hidden rounded-md border">
                          <img src={img.url} alt={img.filename} className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <label className={cn(
                        "flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed transition-colors hover:border-primary/50 hover:bg-accent",
                        isUploadingImage && "pointer-events-none opacity-50"
                      )}>
                        {isUploadingImage ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : (
                          <>
                            <ImagePlus className="h-5 w-5 text-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">Adicionar</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          multiple
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={isUploadingImage}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">As imagens serão rotacionadas junto com as URLs a cada envio.</p>
                    {uploadError && (
                      <p className="text-xs text-destructive">{uploadError}</p>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Fuso horário</Label>
                      <Select value={recurringTimezone} onValueChange={(v) => setRecurringTimezone(v as RecurringTimezone)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRING_TIMEZONES.map((tz) => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Idioma da copy (IA)</Label>
                      <Select value={recurringCopyLanguage} onValueChange={(v) => setRecurringCopyLanguage(v as CopyLanguage)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COPY_LANGUAGES.map((lang) => (
                            <SelectItem key={lang.value} value={lang.value}>
                              {lang.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Horários de envio ({timezoneAbbreviation(recurringTimezone)})</Label>
                    <div className="grid grid-cols-8 gap-1.5">
                      {AVAILABLE_HOURS.map((hour) => (
                        <button
                          key={hour}
                          type="button"
                          onClick={() => toggleHour(hour)}
                          className={cn(
                            "rounded-md border px-1 py-1.5 text-xs font-medium transition-colors",
                            recurringHours.includes(hour)
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input bg-background hover:bg-accent"
                          )}
                        >
                          {formatHour(hour)}
                        </button>
                      ))}
                    </div>
                    {recurringHours.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {recurringHours.length} envio{recurringHours.length > 1 ? "s" : ""} por dia: {recurringHours.map(formatHour).join(", ")}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Data de término (opcional)</Label>
                    <Input
                      type="date"
                      value={recurringEndDate}
                      onChange={(e) => setRecurringEndDate(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">Deixe vazio para enviar indefinidamente.</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Público-alvo</Label>
                    <Select
                      value={recurringTargetType}
                      onValueChange={(v: "all" | "segment") => {
                        setRecurringTargetType(v)
                        if (v === "all") setRecurringSegmentIds([])
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os inscritos</SelectItem>
                        <SelectItem value="segment">Segmento específico</SelectItem>
                      </SelectContent>
                    </Select>
                    {recurringTargetType === "segment" && (
                      <div className="rounded-md border p-3 space-y-2">
                        {segmentsQuery.isLoading ? (
                          <p className="text-xs text-muted-foreground">Carregando segmentos...</p>
                        ) : segments.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Nenhum segmento criado. Crie na página Segmentos.</p>
                        ) : (
                          segments.map(seg => (
                            <label key={seg.id} className="flex items-center gap-2 text-xs">
                              <Checkbox
                                checked={recurringSegmentIds.includes(seg.id)}
                                onCheckedChange={(checked) => {
                                  if (checked === true) {
                                    setRecurringSegmentIds(prev => [...new Set([...prev, seg.id])])
                                  } else {
                                    setRecurringSegmentIds(prev => prev.filter(id => id !== seg.id))
                                  }
                                }}
                              />
                              {seg.name}
                              {seg.subscriber_count != null && (
                                <span className="text-muted-foreground">({seg.subscriber_count.toLocaleString()})</span>
                              )}
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {createRecurringMutation.isError ? (
                    <p className="text-sm text-destructive">{createRecurringMutation.error.message}</p>
                  ) : null}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsRecurringDialogOpen(false)} disabled={createRecurringMutation.isPending}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createRecurringMutation.isPending || !recurringName.trim() || !recurringUrls.trim() || recurringHours.length === 0}
                  >
                    {createRecurringMutation.isPending ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Criando...</>
                    ) : (
                      <><RefreshCw className="mr-2 h-4 w-4" />Criar Recorrente</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="gap-2">
              <Plus className="h-5 w-5" />
              Novo Broadcast
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Criar Novo Broadcast</DialogTitle>
                <DialogDescription>
                  Dê um nome descritivo ao seu broadcast. Você pode alterá-lo depois.
                </DialogDescription>
              </DialogHeader>
              <div className="py-6">
                <Input
                  placeholder="ex.: Série de Boas-vindas, Lançamento, Newsletter Mensal"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  autoFocus
                  className="text-base"
                />
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Canal</p>
                  <Select value={newChannel} onValueChange={(value: WorkflowChannel) => setNewChannel(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="push">Push notification</SelectItem>
                      <SelectItem value="facebook_message">Facebook Messenger</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {createMutation.isError ? (
                  <p className="mt-2 text-sm text-destructive">
                    {createMutation.error.message}
                  </p>
                ) : null}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={createMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || !newName.trim()}
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Criar Broadcast
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Error States */}
      {!currentProject ? (
        <div className="rounded-lg border-2 border-dashed bg-muted/20 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            Selecione um projeto para ver os broadcasts
          </p>
        </div>
      ) : null}

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">
            {error?.message ?? "Falha ao carregar broadcasts."}
          </p>
        </div>
      ) : null}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {/* Empty State */}
      {!isLoading && !isError && workflows.length === 0 && currentProject ? (
        <div className="rounded-lg border-2 border-dashed bg-muted/20 p-12 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <WorkflowIcon className="h-10 w-10 text-primary" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">Nenhum broadcast ainda</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
            Comece criando seu primeiro broadcast. Projete sequências de mensagens, adicione atrasos e automatize suas transmissões.
          </p>
          <Button
            size="lg"
            className="mt-6 gap-2"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="h-5 w-5" />
            Criar Seu Primeiro Broadcast
          </Button>
        </div>
      ) : null}

      {/* Workflows List */}
      {!isLoading && !isError && workflows.length > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {workflows.length} {workflows.length === 1 ? "broadcast" : "broadcasts"}
            </p>
          </div>

          <div className="space-y-3">
            {workflows.map((workflow) => (
              <div
                key={workflow.id}
                className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/40"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold truncate">{workflow.name}</h3>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
                          getStatusColor(workflow.status)
                        )}
                      >
                        {getStatusIcon(workflow.status)}
                        <span className="capitalize">
                          {workflow.status === "active" ? "ativo" : workflow.status === "draft" ? "rascunho" : workflow.status}
                        </span>
                      </span>
                      <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium">
                        {channelLabel(workflow.channel)}
                      </span>
                      {workflow.workflow_type === "recurring_broadcast" && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                          <RefreshCw className="h-3 w-3" />
                          Recorrente
                        </span>
                      )}
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span>ID: {workflow.id}</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        Criado em {formatDate(workflow.created_at)}
                      </span>
                      {workflow.workflow_type === "recurring_broadcast" && workflow.dispatch_count != null && workflow.dispatch_count > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <RefreshCw className="h-3.5 w-3.5" />
                          {workflow.dispatch_count} envios
                        </span>
                      )}
                      {workflow.workflow_type === "recurring_broadcast" && workflow.last_dispatched_at && (
                        <span className="inline-flex items-center gap-1">
                          <Play className="h-3.5 w-3.5" />
                          Último envio {formatDate(workflow.last_dispatched_at)}
                        </span>
                      )}
                      {workflow.workflow_type === "recurring_broadcast" && workflow.schedule_hours && workflow.schedule_hours.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Horários ({timezoneAbbreviation(workflow.timezone ?? DEFAULT_TIMEZONE)}): {workflow.schedule_hours.map(h => formatHour(h)).join(", ")}
                        </span>
                      )}
                      {workflow.workflow_type === "recurring_broadcast" && workflow.copy_language && (
                        <span>{copyLanguageLabel(workflow.copy_language)}</span>
                      )}
                      {workflow.workflow_type !== "recurring_broadcast" && workflow.latest_run && (
                        <span className="inline-flex items-center gap-1">
                          <Play className="h-3.5 w-3.5" />
                          Última execução {formatDate(workflow.latest_run.started_at)}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div className="rounded-lg bg-muted/50 p-2 text-center">
                        <p className="text-xs text-muted-foreground">Audiência</p>
                        <p className="mt-0.5 text-base font-bold">
                          {(workflow.estimated_recipients ?? 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-lg bg-blue-50 p-2 text-center">
                        <p className="text-xs text-blue-600">Enviados</p>
                        <p className="mt-0.5 text-base font-bold text-blue-700">
                          {(workflow.workflow_type === "recurring_broadcast"
                            ? workflow.recurring_metrics?.sent ?? 0
                            : workflow.latest_run?.metrics?.sent ?? 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-lg bg-green-50 p-2 text-center">
                        <p className="text-xs text-green-600">Entregues</p>
                        <p className="mt-0.5 text-base font-bold text-green-700">
                          {(workflow.workflow_type === "recurring_broadcast"
                            ? workflow.recurring_metrics?.delivered ?? 0
                            : workflow.latest_run?.metrics?.delivered ?? 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-lg bg-indigo-50 p-2 text-center">
                        <p className="text-xs text-indigo-600">Cliques</p>
                        <p className="mt-0.5 text-base font-bold text-indigo-700">
                          {(workflow.workflow_type === "recurring_broadcast"
                            ? workflow.recurring_metrics?.clicked ?? 0
                            : workflow.latest_run?.metrics?.clicked ?? 0
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 md:ml-4 md:self-start">
                    {workflow.workflow_type === "recurring_broadcast" ? (
                      <Button variant="default" size="sm" className="gap-2" asChild>
                        <Link to={`/${projectSlug}/broadcasts/${workflow.id}/recurring`}>
                          <RefreshCw className="h-4 w-4" />
                          Detalhe
                        </Link>
                      </Button>
                    ) : (
                      <Button variant="default" size="sm" className="gap-2" asChild>
                        <Link to={`/${projectSlug}/broadcasts/${workflow.id}/run`}>
                          <BarChart3 className="h-4 w-4" />
                          Análises
                        </Link>
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="px-2">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {workflow.workflow_type !== "recurring_broadcast" && (
                          <DropdownMenuItem asChild>
                            <Link to={`/${projectSlug}/broadcasts/${workflow.id}/builder`} className="flex items-center gap-2">
                              <Settings2 className="h-4 w-4" />
                              Construtor
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {workflow.workflow_type === "recurring_broadcast" && (
                          <>
                            <DropdownMenuItem asChild>
                              <Link to={`/${projectSlug}/broadcasts/${workflow.id}/recurring`} className="flex items-center gap-2">
                                <Settings2 className="h-4 w-4" />
                                Editar
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="gap-2"
                              onClick={() => toggleStatusMutation.mutate({
                                id: workflow.id,
                                status: workflow.status === "active" ? "draft" : "active"
                              })}
                              disabled={toggleStatusMutation.isPending}
                            >
                              {workflow.status === "active" ? (
                                <><PowerOff className="h-4 w-4" />Desativar</>
                              ) : (
                                <><Power className="h-4 w-4" />Ativar</>
                              )}
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => setRenameWorkflow({ id: workflow.id, name: workflow.name })}
                        >
                          <Pencil className="h-4 w-4" />
                          Renomear
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => duplicateMutation.mutate(workflow.id)}
                          disabled={duplicateMutation.isPending}
                        >
                          {duplicateMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="gap-2 text-destructive focus:text-destructive"
                          onClick={() => setDeleteWorkflowId(workflow.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Deletar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <Dialog open={renameWorkflow !== null} onOpenChange={(open) => { if (!open) setRenameWorkflow(null) }}>
        <DialogContent className="sm:max-w-[400px]">
          <form onSubmit={(e) => { e.preventDefault(); if (renameWorkflow && renameWorkflow.name.trim()) renameMutation.mutate(renameWorkflow) }}>
            <DialogHeader>
              <DialogTitle>Renomear broadcast</DialogTitle>
              <DialogDescription>Informe o novo nome para este broadcast.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={renameWorkflow?.name ?? ""}
                onChange={(e) => setRenameWorkflow((prev) => prev ? { ...prev, name: e.target.value } : null)}
                autoFocus
                className="text-base"
              />
              {renameMutation.isError ? (
                <p className="mt-2 text-sm text-destructive">{renameMutation.error.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameWorkflow(null)} disabled={renameMutation.isPending}>
                Cancelar
              </Button>
              <Button type="submit" disabled={renameMutation.isPending || !renameWorkflow?.name.trim()}>
                {renameMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteWorkflowId !== null} onOpenChange={(open) => { if (!open) setDeleteWorkflowId(null) }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Deletar broadcast?</DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. O broadcast e todos os seus dados serão removidos permanentemente.
            </DialogDescription>
          </DialogHeader>
          {deleteMutation.isError ? (
            <p className="text-sm text-destructive">{deleteMutation.error.message}</p>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteWorkflowId(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteWorkflowId !== null && deleteMutation.mutate(deleteWorkflowId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Deletar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default BroadcastsPage
